import { z } from 'zod';

/**
 * Códigos de error estables.
 *
 * El frontend decide qué hacer según el `code`, nunca según el texto del
 * `message`. Traducir los mensajes o cambiar su redacción no debe romper la
 * app: por eso el código es un enum cerrado y el mensaje es solo para humanos.
 */
export const errorCodeSchema = z.enum([
  'VALIDATION_ERROR',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'CONFLICT',
  'RATE_LIMITED',
  'SERVICE_UNAVAILABLE',
  'SESSION_REVOKED',
  'INTERNAL_ERROR',
]);

/**
 * SERVICE_UNAVAILABLE (503) existe para diferenciarse de INTERNAL_ERROR (500):
 * un 503 significa "no estoy listo, reintenta dentro de un rato", que es un
 * estado normal y recuperable. Un 500 significa "algo se ha roto en el código",
 * que no se arregla reintentando. El frontend decide qué hacer según el código,
 * así que confundirlos hace que la app se comporte mal justo cuando la base de
 * datos tiene un hipo.
 *
 * SESSION_REVOKED (401) se separa de un 401 genérico porque el usuario NECESITA
 * saber la diferencia: "tu sesión caducó, vuelve a entrar" es rutina, pero
 * "se han cerrado todas tus sesiones por seguridad" es un aviso de que puede
 * haber alguien más usando su cuenta. Si ambos llegan como un 401 sin más, el
 * frontend los trata igual y el usuario se entera por el logout, que es
 * justo el escenario en el que debe sospechar.
 *
 * @typedef {'VALIDATION_ERROR' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND'
 *   | 'CONFLICT' | 'RATE_LIMITED' | 'SERVICE_UNAVAILABLE' | 'SESSION_REVOKED'
 *   | 'INTERNAL_ERROR'} ErrorCode
 */

/**
 * @typedef {object} ApiError
 * @property {object} error
 * @property {ErrorCode} error.code
 * @property {string} error.message
 * @property {Record<string, string[]>} [error.fieldErrors]
 * @property {string} [error.requestId]
 * @property {string} [error.detail] Solo en desarrollo.
 */

/**
 * Formato único de error de la API. Todo error sale con esta forma, sin
 * excepciones, porque el cliente debe poder parsearlo sin adivinar.
 */
export const apiErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    /** Errores por campo, para pintar formularios. Clave = nombre del campo. */
    fieldErrors: z.record(z.string(), z.array(z.string())).optional(),
    requestId: z.string().optional(),
    detail: z.string().optional(),
  }),
});

/** Identificador de contenido: "w01-l03", "w01-l03-e2", "w01-l03-q1". */
export const contentIdSchema = z.string().min(1).max(64);

/**
 * Identificador generado por Prisma con `@default(cuid())`.
 *
 * Vive aquí porque aparece en casi todos los schemas de respuesta. Definirlo
 * una vez evita que cada módulo invente su propia versión con un regex
 * ligeramente distinto.
 *
 * El patrón exige el prefijo `c` y una longitud mínima: un regex laxo como
 * `/^[a-z0-9]+$/` aceptaría cualquier texto y no estaría validando nada.
 */
export const cuidSchema = z.string().regex(/^c[a-z0-9]{20,32}$/, 'Identificador no válido');

/** Fecha en ISO 8601. En el JSON de la API las fechas SIEMPRE viajan como texto. */
export const isoDateSchema = z.string().datetime();

/** Fecha de día natural, sin hora: "YYYY-MM-DD". */
export const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato esperado: YYYY-MM-DD');

/** Semana ISO: "2026-W14". */
export const isoWeekSchema = z.string().regex(/^\d{4}-W\d{2}$/, 'Formato esperado: AAAA-Www');
